import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters, internalPath } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import usePackWallets from "/imports/hooks/usePackWallets";
import { saveResumeIntent } from "/imports/libs/resumeIntent";
import { packMemberPricing, memberPriceNote } from "/imports/libs/memberPricing";
import { formatExpiry } from "../packagesHelpers";
import { clubRulesForCategoryType } from "/imports/libs/bookingRules";
import useClubBookingCancel from "/imports/hooks/useClubBookingCancel";
import { wall, windowEnd } from "./upfrontSessionsHelpers";

/**
 * Session picker for packages sold with upfront booking: the client chooses
 * which class dates the pack starts with ("partial") or all of them ("all"),
 * the seats are held while they pay, and payment books them.
 */
function useUpfrontSessions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, saas, locations } = useWidget();
  const { isAuthenticated } = useAuth();
  const { filters } = useUrlFilters({});

  const productId = filters.product || null;
  const variantKey = filters.variant || null;
  const pricingId = filters.pricingId || null;
  const locationId = filters.locationId || null;
  const preselect = filters.preselect || null;
  const backTo = internalPath(filters.from);

  // The catalog is the authority on the variant (mode, anchor, expiry, price) —
  // never trust what a crafted URL claims about it.
  const { data: catalog, loading: catalogLoading, error: catalogError } = useFetch(() => api.listPackagesForSale(slug), [slug], { skip: !slug });
  const pkg = (catalog?.items || []).find((p) => p._id === productId) || null;
  const opt = (pkg?.pricingOptions || []).find((o) => (variantKey ? o.variantKey === variantKey : o.pricingId === pricingId)) || null;

  const mode = opt?.upfrontBooking;
  const sessionsN = opt?.sessions || 0;
  const anchor = opt?.upfrontAnchor || "saleDate";
  const minPicks = mode === "all" ? sessionsN : 1;

  // Private-lesson / court packs have no timetable to tick — they get the
  // slot-composer builder instead (rendered below, after every hook).
  const isComposerCategory = ["category-service-private", "category-service-court"].includes(opt?.linkedCategoryType);

  const { data: memberStatus } = useFetch(() => api.getPurchaseMembershipStatus(slug), [slug], { skip: !slug || !isAuthenticated });

  // The client's earliest pick, held as its own state because the timetable
  // that produces it is fetched with it — see the horizon below. Kept in sync
  // by an effect once the selection settles.
  const [pickAnchorDate, setPickAnchorDate] = useState(null);

  // How far ahead the picker looks.
  //
  // Until the client picks, the club's public booking window is the authority
  // and the server trims to it, so a generous ceiling here costs nothing. The
  // FIRST pick changes that: buying the pack is a commitment, so the rest of
  // the sessions reach as far as the pack itself is valid — a 2-month pack
  // whose first session lands on the last day the window opens must still be
  // able to book the other four. The server re-resolves the same deadline from
  // the product before it hands any of those later sessions over.
  const hasExpiry = !!(opt && !opt.noExpiry && opt.expiryDuration && opt.expiryDurationUnit);
  const horizon = useMemo(() => {
    const today = DateTime.now().startOf("day");
    const base = hasExpiry && anchor === "saleDate" ? windowEnd(today, opt.expiryDuration, opt.expiryDurationUnit) : today.plus({ days: 180 });
    if (!pickAnchorDate || !hasExpiry || anchor !== "firstSession") return base;
    const fromPick = windowEnd(DateTime.fromISO(pickAnchorDate, { zone: "utc" }), opt.expiryDuration, opt.expiryDurationUnit);
    return fromPick > base ? fromPick : base;
  }, [opt, anchor, hasExpiry, pickAnchorDate]);

  const [reloadKey, setReloadKey] = useState(0);
  const {
    data: timetable,
    loading: sessionsLoading,
    error: sessionsError
  } = useFetch(
    () =>
      api.getClassTimetable(slug, {
        serviceId: opt.linkedServiceId,
        locationId: locationId || undefined,
        dateStart: DateTime.now().toISODate(),
        dateEnd: horizon.toISODate(),
        // Always named, so the timetable knows it is serving a PURCHASE: a pack
        // the client already owns must not widen this list — those seats will be
        // held against the pack being bought, and the club's window still gates
        // the first pick.
        packProduct: pkg?._id,
        packVariant: opt.variantKey,
        // What earns the dates past that window. Sent only once a first session
        // is picked — that pick is what the new pack's validity, and so the
        // extension, is measured from.
        packFirstDate: pickAnchorDate || undefined
      }),
    [slug, opt?.linkedServiceId, locationId, reloadKey, pickAnchorDate],
    { skip: !slug || !opt?.linkedServiceId || isComposerCategory }
  );

  // Shared packs may cover fewer locations than the widget serves.
  const sessions = useMemo(() => {
    let list = timetable?.classes || [];
    if (!locationId && (pkg?.locationIds || []).length > 0) {
      list = list.filter((c) => !c.location?._id || pkg.locationIds.includes(c.location._id));
    }
    return list;
  }, [timetable, pkg, locationId]);

  // A class the club already put this client in and never charged for is the one
  // kind of "already booked" that is still pickable: the pack pays for the seat
  // they are sitting in. Seats and level don't gate it — they hold the seat, and
  // the club chose to put them in the class.
  const baseSelectable = (c) =>
    c.joinedUnbilled || (!c.isJoined && (c.spotsAvailable == null || c.spotsAvailable > 0) && c.eligibility?.eligible !== false);

  // Level-ineligible sessions are hidden rather than greyed out. The client's level is
  // fixed for the length of this purchase, so those cards can never become pickable —
  // a timetable that is mostly dead cards reads as "nothing here for me". Full and
  // outside-the-window stay visible: both are true for everyone, both change on their
  // own, and both explain a gap the client can otherwise see in the week.
  const visibleSessions = useMemo(() => sessions.filter((c) => c.joinedUnbilled || c.eligibility?.eligible !== false), [sessions]);

  const hiddenCount = sessions.length - visibleSessions.length;

  // Hiding removes the explanation along with the card, so the view has to be able to
  // say why the list is short — and, when the client has simply never been assessed,
  // what to do about it.
  const eligibleCount = useMemo(() => visibleSessions.filter(baseSelectable).length, [visibleSessions]);
  const needsAssessment = useMemo(() => sessions.some((c) => c.eligibility?.needsAssessment), [sessions]);

  // A client assessed at two levels in this sport is picking from a list that
  // mixes both, and the pack is spent on whichever dates they choose — so each
  // card has to say which level it is. One level and every card carries the
  // same badge, which tells the client nothing.
  const showSessionLevel = useMemo(() => {
    const mine = timetable?.filters?.myLevels || [];
    const sportIds = new Set(sessions.map((c) => c.sport).filter(Boolean));
    return [...sportIds].some((id) => (mine.find((l) => l.sportId === id)?.levelValues || []).length > 1);
  }, [sessions, timetable]);

  // Venue filter. Display-only: it narrows which cards are on show, and never
  // touches the URL's locationId — that one scopes the purchase itself and is
  // set by the plan the client came from.
  const [venueFilter, setVenueFilter] = useState(null);

  const locationOptions = useMemo(() => {
    const byId = new Map();
    for (const c of visibleSessions) {
      if (c.location?._id && !byId.has(c.location._id)) byId.set(c.location._id, { _id: c.location._id, name: c.location.name || "Location" });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleSessions]);

  // A widened horizon can bring back a list the chosen venue isn't in — drop the
  // filter rather than showing an empty timetable under a venue name.
  useEffect(() => {
    if (venueFilter && !locationOptions.some((l) => l._id === venueFilter)) setVenueFilter(null);
  }, [locationOptions, venueFilter]);

  const [selected, setSelected] = useState(() => new Set(preselect ? [preselect] : []));
  const selectedSessions = useMemo(
    () => sessions.filter((c) => selected.has(c._id)).sort((a, b) => (a.startDate < b.startDate ? -1 : 1)),
    [sessions, selected]
  );

  // Picks stay on the list whatever the venue filter says: a card that vanished
  // while it was still counted in "3 of 5" reads as a lost selection.
  const displaySessions = useMemo(
    () => (venueFilter ? visibleSessions.filter((c) => c.location?._id === venueFilter || selected.has(c._id)) : visibleSessions),
    [visibleSessions, venueFilter, selected]
  );

  // The earliest pick drives the fetch above, so it can't be read straight off
  // the selection there — mirror it into state once the selection settles.
  useEffect(() => {
    const next = selectedSessions[0] ? wall(selectedSessions[0].startDate).toISODate() : null;
    setPickAnchorDate((cur) => (cur === next ? cur : next));
  }, [selectedSessions]);

  // Dropping the earliest pick moves the anchor forward, and the server may
  // then refuse to widen at all — the extension is earned by a first session
  // inside the club's window, and the new earliest may sit outside it. The list
  // comes back short, so picks that are no longer on it have to go, and the
  // client has to be told why rather than watching their count fall on its own.
  useEffect(() => {
    if (sessionsLoading || sessions.length === 0 || selected.size === 0) return;
    const onList = new Set(sessions.map((c) => c._id));
    const dropped = [...selected].filter((id) => !onList.has(id));
    if (dropped.length === 0) return;

    setSelected(new Set([...selected].filter((id) => onList.has(id))));
    enqueueSnackbar(
      dropped.length === 1
        ? "One of your sessions is outside what this pack can book from your first date — it's been removed"
        : `${dropped.length} of your sessions are outside what this pack can book from your first date — they've been removed`,
      { variant: "warning" }
    );
  }, [sessions, sessionsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sessions the club booked for this client and never charged for: tick them on
  // arrival, earliest first, so someone who was booked five classes ago lands on a
  // full selection and only has to pay. Once — unticking is theirs to do, and the
  // pack is still theirs to spend on other dates.
  //
  // A ?preselect= in the URL is a deliberate choice made on the page they came
  // from, so it wins and this stays out of the way.
  const seededExisting = useRef(false);
  const [existingTicked, setExistingTicked] = useState(false);
  useEffect(() => {
    if (seededExisting.current || sessionsLoading || sessions.length === 0) return;
    seededExisting.current = true;
    if (preselect) return;

    const unbilled = sessions.filter((c) => c.joinedUnbilled).sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
    if (unbilled.length === 0) return;

    const kept = [];
    let pastValidity = 0;
    for (const c of unbilled) {
      if (kept.length >= sessionsN) break;
      // One pack, one validity window. A firstSession pack is measured from the
      // earliest class ticked here, so a booking further out than that window is one
      // this pack can never pay for — leave it, rather than tick something checkout
      // would refuse.
      if (kept.length > 0 && anchor === "firstSession" && hasExpiry) {
        const deadline = windowEnd(wall(kept[0].startDate).startOf("day"), opt.expiryDuration, opt.expiryDurationUnit);
        if (wall(c.startDate) > deadline) {
          pastValidity += 1;
          continue;
        }
      }
      kept.push(c);
    }
    if (kept.length === 0) return;

    setSelected(new Set(kept.map((c) => c._id)));
    setExistingTicked(true);
    const ticked =
      kept.length === 1
        ? "The club already booked you into one of these classes — we've ticked it so this pack pays for it"
        : `The club already booked you into ${kept.length} of these classes — we've ticked them so this pack pays for them`;
    // Said out loud, or the client pays and finds classes of theirs still unpaid with
    // nothing on the page saying which, or why.
    const trimmed =
      pastValidity > 0
        ? `. ${pastValidity} more ${pastValidity === 1 ? "is" : "are"} further out than one pack reaches — ${
            pastValidity === 1 ? "it needs" : "they need"
          } a second pack`
        : "";
    enqueueSnackbar(`${ticked}${trimmed}`, { variant: "info" });
  }, [sessions, sessionsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // First-session anchor: every pick must fit one validity window together.
  // (Sale-date anchor needs none of this — the horizon above already stops the
  // list at the deadline.)
  const windowCfg =
    anchor === "firstSession" && opt && !opt.noExpiry && opt.expiryDuration && opt.expiryDurationUnit
      ? { dur: opt.expiryDuration, unit: opt.expiryDurationUnit }
      : null;

  // Would adding this session keep the whole selection inside one validity
  // window? Checked in both directions — an EARLIER pick moves the window's
  // start back and can push the latest pick outside it.
  const fitsWindow = (c) => {
    if (!windowCfg || selected.has(c._id) || selectedSessions.length === 0) return true;
    const s = wall(c.startDate);
    const first = wall(selectedSessions[0].startDate);
    const last = wall(selectedSessions[selectedSessions.length - 1].startDate);
    const start = (s < first ? s : first).startOf("day");
    const end = s > last ? s : last;
    return end <= windowEnd(start, windowCfg.dur, windowCfg.unit);
  };

  const selectable = (c) => baseSelectable(c) && fitsWindow(c);

  // First-session anchor: the picks must all fit inside one validity window
  // measured from the earliest pick. (Sale-date anchor needs no check here —
  // the horizon above already stops at the deadline.)
  const spanError = useMemo(() => {
    if (anchor !== "firstSession" || !opt || opt.noExpiry || !opt.expiryDuration || !opt.expiryDurationUnit) return null;
    if (selectedSessions.length < 2) return null;
    const first = wall(selectedSessions[0].startDate).startOf("day");
    const deadline = windowEnd(first, opt.expiryDuration, opt.expiryDurationUnit);
    const outside = selectedSessions.filter((c) => wall(c.startDate) > deadline);
    if (outside.length === 0) return null;
    return `Your pack is valid ${opt.expiryDuration} ${opt.expiryDurationUnit} from your first session — sessions after ${deadline.toFormat(
      "d LLL yyyy"
    )} don't fit`;
  }, [anchor, opt, selectedSessions]);

  const unselect = (id) =>
    setSelected((cur) => {
      const next = new Set(cur);
      next.delete(id);
      return next;
    });

  const clubCancel = useClubBookingCancel({
    onCancelled: ({ classId }) => {
      unselect(classId);
      // Refetched so the class comes back as an ordinary card with its seat free.
      setReloadKey((k) => k + 1);
    },
    onLeftOut: ({ classId }) => unselect(classId)
  });

  const openClassCancel = (c, outsidePack) =>
    clubCancel.openFor({
      kind: "class",
      outsidePack,
      classId: c._id,
      bookishId: c._id,
      label: [wall(c.startDate).toFormat("ccc d LLL"), `at ${c.time}`, c.trainer?.name ? `with ${c.trainer.name}` : null].filter(Boolean).join(" ")
    });

  // A club-booked class this pack isn't paying for is just as unpaid, and the
  // client may simply not be around for it.
  const requestCancel = (c) => openClassCancel(c, true);

  const toggle = (c) => {
    // Unticking a class the club booked asks to cancel it rather than quietly
    // leaving the seat taken and unpaid.
    if (selected.has(c._id) && c.joinedUnbilled) {
      openClassCancel(c, false);
      return;
    }
    if (!selectable(c) && !selected.has(c._id)) return;
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(c._id)) {
        next.delete(c._id);
      } else {
        if (next.size >= sessionsN) {
          enqueueSnackbar(`This pack covers ${sessionsN} sessions — unselect one first`, { variant: "info" });
          return cur;
        }
        next.add(c._id);
      }
      return next;
    });
  };

  // Club-booked classes this pack could pay for but currently isn't. They sit
  // scattered through a long timetable, so the bar lists them in one place to
  // add or cancel. Only those inside the pack's validity: one past it can never
  // be added, and offering it here reads as if it could.
  const notIncluded = useMemo(
    () => sessions.filter((c) => c.joinedUnbilled && !selected.has(c._id) && fitsWindow(c)).sort((a, b) => (a.startDate < b.startDate ? -1 : 1)),
    [sessions, selected, selectedSessions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Same count rule as ticking the card, but said out loud — from the bar the
  // client can't see why nothing happened.
  const addClubClass = (c) => {
    if (selected.size >= sessionsN) {
      enqueueSnackbar(`This pack covers ${sessionsN} sessions — unselect one first`, { variant: "info" });
      return;
    }
    toggle(c);
  };

  // Classes the club booked stay: removing one cancels it, which is asked one at
  // a time, never swept away with the client's own picks.
  const clearPicks = () => setSelected(new Set(selectedSessions.filter((c) => c.joinedUnbilled).map((c) => c._id)));

  // "Next N": the soonest bookable occurrences, replacing the client's own
  // picks. Classes the club booked stay ticked — dropping one leaves the seat
  // taken and unpaid, so that only happens through the cancel dialog. Free
  // places go to the club's other unpaid bookings first: the client already
  // holds those seats, and an earlier fresh class would otherwise shrink the
  // pack's window until they no longer fit. Fresh classes fill what is left,
  // all inside one validity window.
  const selectNext = () => {
    const picked = selectedSessions.filter((c) => c.joinedUnbilled);
    const fitsWithPicked = (c) => {
      if (!windowCfg || picked.length === 0) return true;
      const dates = [...picked, c].map((x) => wall(x.startDate));
      const first = DateTime.min(...dates).startOf("day");
      return DateTime.max(...dates) <= windowEnd(first, windowCfg.dur, windowCfg.unit);
    };
    const byDate = (x, y) => (x.startDate < y.startDate ? -1 : 1);
    const clubBooked = sessions.filter((c) => c.joinedUnbilled && !picked.includes(c)).sort(byDate);
    const fresh = sessions.filter((c) => !c.joinedUnbilled && baseSelectable(c) && (!venueFilter || c.location?._id === venueFilter)).sort(byDate);
    for (const c of [...clubBooked, ...fresh]) {
      if (picked.length >= sessionsN) break;
      if (fitsWithPicked(c)) picked.push(c);
    }
    setSelected(new Set(picked.map((c) => c._id)));
  };

  // "Repeat weekly": walk forward from the latest pick, adding the same
  // weekday + time week after week until the pack is full or the list runs out.
  const lastPick = selectedSessions[selectedSessions.length - 1] || null;
  const repeatWeekly = () => {
    if (!lastPick) return;
    const next = new Set(selected);
    // The walk only moves forward, so the window is bounded by the earliest
    // existing pick — stop before stepping past its validity deadline.
    const deadline = windowCfg ? windowEnd(wall(selectedSessions[0].startDate).startOf("day"), windowCfg.dur, windowCfg.unit) : null;
    let cursor = wall(lastPick.startDate);
    while (next.size < sessionsN) {
      cursor = cursor.plus({ days: 7 });
      if (deadline && cursor > deadline) break;
      const match = sessions.find(
        (c) =>
          !next.has(c._id) &&
          baseSelectable(c) &&
          wall(c.startDate).hasSame(cursor, "day") &&
          c.time === lastPick.time &&
          (!lastPick.location?._id || c.location?._id === lastPick.location._id)
      );
      if (!match) break;
      next.add(match._id);
    }
    if (next.size === selected.size) {
      enqueueSnackbar("No matching weekly sessions found ahead", { variant: "info" });
      return;
    }
    setSelected(next);
  };

  // A book-all pack is member-priced only while every picked class is at a
  // branch the client is a member at — one class elsewhere and it's full price.
  const memberPricing = packMemberPricing({
    pkg,
    opt,
    memberStatus,
    purchaseLocationId: locationId,
    sessionLocationIds: selectedSessions.map((c) => c.location?._id || null)
  });
  const effectivePrice = memberPricing.price;
  const memberNote = memberPriceNote({
    pricing: memberPricing,
    opt,
    currency,
    nameOf: (id) => (locations || []).find((l) => l._id === id)?.name
  });

  const alreadyBookedCount = useMemo(() => selectedSessions.filter((c) => c.joinedUnbilled).length, [selectedSessions]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  const wallets = usePackWallets({ open: confirmOpen, locationId, price: effectivePrice });

  const doPurchase = async ({ voucherId } = {}) => {
    setBuying(true);
    try {
      const result = await api.purchasePackage(slug, {
        productId: pkg._id,
        pricingId: opt.pricingId,
        variantKey: opt.variantKey,
        sessions: opt.sessions,
        sessionDuration: opt.sessionDuration,
        linkedServiceId: opt.linkedServiceId,
        locationId: locationId || undefined,
        // Two lists, one purchase: classes to book now, and seats the client already
        // holds that this pack is paying for. The server counts them together against
        // the variant's demand.
        upfrontBookishIds: selectedSessions.filter((c) => !c.joinedUnbilled).map((c) => c._id),
        upfrontBcIds: selectedSessions.filter((c) => c.joinedUnbilled).map((c) => c.joinedBookingClientId),
        voucherId: voucherId || undefined
      });
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        if (backTo) saveResumeIntent({ slug, backTo });
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete — your sessions are booked", { variant: "success" });
      navigate(`/${slug}/dashboard?tab=bookings&scope=upcoming`);
    } catch (err) {
      setConfirmOpen(false);
      // A pick that just filled up (or is already yours) comes back with its
      // session id — drop it and refresh the list so the client re-picks.
      const conflictId = err.data?.data?.bookishId;
      // An existing booking is named by its BookishClient id, so map it back to the
      // class the client is looking at before dropping it.
      const conflictBcId = err.data?.data?.bookishClientId;
      const dropId = conflictId || sessions.find((c) => c.joinedBookingClientId === conflictBcId)?._id;
      if (dropId) {
        setSelected((cur) => {
          const next = new Set(cur);
          next.delete(dropId);
          return next;
        });
      }
      if (dropId || conflictBcId) setReloadKey((k) => k + 1);
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuying(false);
    }
  };

  // Buying needs an account; send visitors through login with the way back
  // (an effect, not render-time — navigating during render is a React error).
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${slug}/login?redirect=${encodeURIComponent(`plans/select-sessions${location.search}`)}`);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // The first pick re-fetches the timetable on a wider horizon. Swapping the
  // whole list for a spinner at that moment would blank the sessions the client
  // just chose from, so a reload that already has sessions behind it is shown
  // as a quiet line instead — see loadingMore.
  const initialLoad = sessionsLoading && sessions.length === 0;
  const loadingMore = sessionsLoading && sessions.length > 0;

  const missingVariant = !catalogLoading && (!pkg || !opt || (mode !== "all" && mode !== "partial"));
  const countLabel = `${selectedSessions.length} of ${sessionsN}`;
  // The club has a timetable, but not enough of it is open to this client to complete
  // the purchase — an "all" pack needs every session at once, and any pack needs one.
  // Without this the button just reads "Pick 3 more" against a list that can't supply
  // them.
  const notEnoughEligible = sessions.length > 0 && eligibleCount < minPicks;

  const canContinue = selectedSessions.length >= minPicks && selectedSessions.length <= sessionsN && !spanError && !buying;

  // Group by month purely for scannability on long horizons.
  const grouped = useMemo(() => {
    const out = [];
    let currentKey = null;
    for (const c of displaySessions) {
      const key = wall(c.startDate).toFormat("LLLL yyyy");
      if (key !== currentKey) {
        currentKey = key;
        out.push({ month: key, items: [] });
      }
      out[out.length - 1].items.push(c);
    }
    return out;
  }, [displaySessions]);

  const confirmLines = opt
    ? [
        [
          opt.sessions ? `${opt.sessions} session${opt.sessions === 1 ? "" : "s"}` : "Package",
          opt.sessionDuration ? `${opt.sessionDuration} min each` : null,
          formatExpiry(opt)
        ]
          .filter(Boolean)
          .join(" · "),
        `${selectedSessions.length} session${selectedSessions.length === 1 ? "" : "s"} booked now${
          mode === "partial" && selectedSessions.length < sessionsN
            ? ` — ${sessionsN - selectedSessions.length} left to book later (subject to availability)`
            : ""
        }`,
        // Named separately: these are classes the client is already in, and paying
        // here is what settles them. Nothing new gets booked for them.
        alreadyBookedCount > 0
          ? `Includes ${alreadyBookedCount} class${alreadyBookedCount === 1 ? "" : "es"} the club already booked for you — this pays for ${
              alreadyBookedCount === 1 ? "it" : "them"
            }`
          : null,
        selectedSessions.length > 0
          ? `${wall(selectedSessions[0].startDate).toFormat("d LLL")} – ${wall(selectedSessions[selectedSessions.length - 1].startDate).toFormat("d LLL yyyy")}`
          : null
      ].filter(Boolean)
    : [];

  const confirmNote = opt && anchor === "firstSession" && !opt.noExpiry ? "Your pack's validity starts at your first session, not today." : null;

  // Why the list stops where it does — and, before the first pick, that it is
  // about to grow. Only for packs measured from the first session: a sale-date
  // pack's deadline never moves, so there is nothing to announce.
  const packWindowNote = useMemo(() => {
    if (!hasExpiry || anchor !== "firstSession") return null;
    if (!pickAnchorDate) return `Pick your first session and the later dates open up — your pack runs ${formatExpiry(opt)} from it.`;
    const deadline = windowEnd(DateTime.fromISO(pickAnchorDate, { zone: "utc" }), opt.expiryDuration, opt.expiryDurationUnit);
    return `Your pack runs to ${deadline.toFormat("d LLL yyyy")} — any session up to then is yours to book.`;
  }, [hasExpiry, anchor, pickAnchorDate, opt]);

  // The club's rules for this pack's category, shown in the picker and again in
  // the confirm dialog the client ticks to accept them.
  const packRules = opt ? clubRulesForCategoryType(saas?.widgetRules, opt.linkedCategoryType) : [];

  // Buying the next pack of a series already booked ahead: the server says where
  // the client's current classes end, and the list opens at the first class from
  // the day after. Earlier classes stay listed for anyone starting sooner.
  const packContinuation = timetable?.packContinuation || null;
  const continueAnchorId = useMemo(() => {
    if (!packContinuation) return null;
    return displaySessions.find((c) => wall(c.startDate).toISODate() >= packContinuation.continueFrom)?._id || null;
  }, [packContinuation, displaySessions]);

  return {
    navigate,
    slug,
    currency,
    isAuthenticated,
    pkg,
    opt,
    locationId,
    backTo,
    mode,
    sessionsN,
    minPicks,
    isComposerCategory,
    catalogLoading,
    catalogError,
    sessionsLoading,
    initialLoad,
    loadingMore,
    sessionsError,
    sessions,
    visibleSessions,
    displaySessions,
    locationOptions,
    venueFilter,
    setVenueFilter,
    hiddenCount,
    eligibleCount,
    needsAssessment,
    showSessionLevel,
    notEnoughEligible,
    grouped,
    selected,
    setSelected,
    selectedSessions,
    existingTicked,
    selectable,
    fitsWindow,
    toggle,
    clearPicks,
    clubCancel,
    requestCancel,
    notIncluded,
    addClubClass,
    selectNext,
    repeatWeekly,
    lastPick,
    spanError,
    memberStatus,
    effectivePrice,
    regularPrice: memberPricing.regularPrice,
    memberNote,
    confirmOpen,
    wallets,
    setConfirmOpen,
    buying,
    doPurchase,
    missingVariant,
    countLabel,
    canContinue,
    confirmLines,
    confirmNote,
    packRules,
    packWindowNote,
    packContinuation,
    continueAnchorId
  };
}

export default useUpfrontSessions;
